import React from 'react';
import './ManagerSparklineTooltip.styl';
import {scaleLinear, scaleTime} from 'd3-scale';
import {extent, max} from 'd3-array';
import Axes from 'components/pure/charts/custom/Axes';
import moment from 'moment';
import {formatDuration} from 'components/dashboard/coaching-effectiveness/common';
import {Link} from 'react-router';
import buildUrl from 'components/dashboard/buildUrl';
import SimpleLineSeries from 'charts/components/series/SimpleLineSeries';
import {bridge} from 'charts';
import {Chart} from 'charts/components/Chart';
import PointSeries from 'charts/components/series/PointSeries';
import {Int} from '@store/@types/util';
import {UserId} from 'records/User';
import {Property} from 'csstype';
import {HISTORY_ACTIONS} from 'components/StatsPage2.utils';

export function formatAsHours(seconds) {
  return formatDuration(moment.duration(seconds, 'seconds'));
}

function formatDurationPrecise(duration) {
  // between 1 and 59 seconds we display second values
  const seconds = duration.asSeconds();
  if (seconds > 0 && seconds < 60) {
    return duration.format('h:mm:ss', {trim: false});
  }
  return duration.format('h:mm', {trim: false});
}

interface SeriesData {
  data: {date: Date; value: number}[];
  color: Property.Color;
  timeType: 'Meeting' | 'Review';
}

interface ManagerSparklineTooltipProps {
  fromPath: string;
  from: Date;
  to: Date;
  series: SeriesData[];
  meetingTotalInSeconds: Int;
  reviewTotalInSeconds: Int;
  adminId?: UserId;
  managerId?: UserId;
}

/**
 * Tooltip for the manager sparkline.
 *
 * Mostly uses components from /src/charts, axes from web project (they handle
 * the time ticks properly on x axis)
 *
 * @param from - beginning of range
 * @param to - end of range
 * @param series - the line series (plural)
 * @param meetingTotalInSeconds - total in seconds of meeting time spent
 * @param reviewTotalInSeconds - total in seconds of review time spent
 */
export default class ManagerSparklineTooltip extends React.Component<ManagerSparklineTooltipProps> {
  render() {
    const {
      fromPath,
      from,
      to,
      series: seriesFromProps,
      meetingTotalInSeconds,
      reviewTotalInSeconds,
    } = this.props;

    const seriesInMinutes = seriesFromProps.map((s, i, seriesArray) => ({
      ...s,
      data: s.data.map(d => {
        // Calculate both Review and Meeting minutes to
        // be able to display both in each series popup.
        const minutes = d.value === 0 ? 0 : d.value / 60;
        const otherSeriesData =
          s.timeType === 'Review'
            ? seriesArray.find(os => os.timeType === 'Meeting')?.data ?? []
            : seriesArray.find(os => os.timeType === 'Review')?.data ?? [];
        const otherSeriesValue = otherSeriesData.find(osd => osd.date === d.date)?.value ?? 0;
        const otherSeriesMinutes = otherSeriesValue === 0 ? 0 : otherSeriesValue / 60;

        return {
          ...d,
          value: minutes,
          reviewTime: s.timeType === 'Review' ? minutes : otherSeriesMinutes,
          meetingTime: s.timeType === 'Meeting' ? minutes : otherSeriesMinutes,
        };
      }),
    }));

    const margins = {left: 50, bottom: 20, top: 0, right: 0};
    const svgDimensions = {height: 125, width: 375};

    const xScale = scaleTime()
      .domain([from, to])
      .range([margins.left, svgDimensions.width - margins.right]);

    const values = seriesInMinutes.map(s => s.data.map(d => d.value)).flat();

    const yScale = scaleLinear()
      .domain(extent([0, Math.max(max(values)! * 1.2, 5)]) as [number, number])
      .range([svgDimensions.height - margins.bottom, margins.top]);

    return (
      <div className="manager-sparkline-tooltip">
        <Chart bordered margins={margins} svgDimensions={svgDimensions} className="expanded-chart">
          <Axes
            scales={{xScale, yScale}}
            margins={margins}
            formatValue={v => formatDurationPrecise(moment.duration(v, 'minutes'))}
          />
          {seriesInMinutes.map((s, i) => (
            <g key={i}>
              <SimpleLineSeries
                key={'l' + i}
                data={bridge(from, to, s.data)}
                color={s.color}
                xScale={xScale}
                yScale={yScale}
              />
              <PointSeries
                key={'p' + i}
                data={s.data}
                color={s.color}
                xScale={xScale}
                yScale={yScale}
                radius={3}
                popContent={d => (
                  <div className="point-detail">
                    <span>Coaching on {moment(d.date).format('MM/DD/YY')}</span>
                    <span className="point-value">
                      Review time:{' '}
                      {d.reviewTime < 1 ? '1 Minute' : `${Math.round(d.reviewTime)} Minute(s)`}
                    </span>
                    <span className="point-value">
                      Meeting time:{' '}
                      {d.meetingTime < 1 ? '1 Minute' : `${Math.round(d.meetingTime)} Minute(s)`}
                    </span>
                  </div>
                )}
              />
            </g>
          ))}
        </Chart>
        <div className="totals">
          <h3>Hours</h3>
          <p className="subtotal">
            <div className="review-line" />
            <span>Review Time: {formatAsHours(reviewTotalInSeconds)}</span>
          </p>
          <p className="subtotal">
            <div className="meeting-line" />
            <span>Meeting Time: {formatAsHours(meetingTotalInSeconds)}</span>
          </p>
          <p className="grand-total">
            <div className="total">
              {formatAsHours(reviewTotalInSeconds + meetingTotalInSeconds)}
            </div>
            <div>hours total</div>
          </p>
          <Link
            className="details-link generic-link"
            to={{
              pathname: buildUrl(this.props) + '/your-coaching',
              state: {
                pathname: fromPath,
                action: HISTORY_ACTIONS.PUSH,
              },
            }}>
            View Details
          </Link>
        </div>
      </div>
    );
  }
}
